import React from "react";
import { motion } from "framer-motion";
import {
    Rocket,
    Users,
    Layers,
    Gem,
    Magnet,
    Bot,
    TrendingUp,
    Radar,
    ArrowUpRight,
    Footprints,
    Sparkle,
} from "lucide-react";
import { PILLARS } from "../constants/content";
import { typeformLinkProps } from "./TypeformModal";
import { scrollToPillar } from "../utils/scroll";

const ICONS = [Rocket, Users, Layers, Gem, Magnet, Bot, TrendingUp, Radar];
const SPANS = [
    "md:col-span-7",
    "md:col-span-5",
    "md:col-span-4",
    "md:col-span-4",
    "md:col-span-4",
    "md:col-span-5",
    "md:col-span-7",
    "md:col-span-12",
];
const ACCENTS = [
    "hover:border-neon-purple/50 hover:shadow-[0_0_40px_rgba(168,85,247,0.15)]",
    "hover:border-neon-pink/50 hover:shadow-[0_0_40px_rgba(236,72,153,0.15)]",
    "hover:border-neon-blue/50 hover:shadow-[0_0_40px_rgba(125,211,252,0.15)]",
];
const ICON_BG = [
    "from-neon-purple/25 to-neon-purple/5 text-neon-purple",
    "from-neon-pink/25 to-neon-pink/5 text-neon-pink",
    "from-neon-blue/25 to-neon-blue/5 text-neon-blue",
];

const PillarsSection = () => {
    return (
        <section id="pillars" data-testid="pillars-section" className="relative py-28 sm:py-36">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <div className="flex flex-wrap items-end justify-between gap-6">
                    <div>
                        <motion.p
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.6 }}
                            className="font-mono-tech text-[11px] uppercase tracking-[0.35em] text-neon-blue"
                        >
                            The 8-Pillar Framework
                        </motion.p>
                        <motion.h2
                            initial={{ opacity: 0, y: 32 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.7, delay: 0.1 }}
                            className="font-display mt-5 text-3xl font-extrabold leading-tight text-white sm:text-5xl"
                        >
                            INSPIRE, IMPACT <span className="text-gradient-neon">AND EARN!</span>
                        </motion.h2>
                    </div>
                    <motion.p
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7, delay: 0.2 }}
                        className="max-w-sm text-sm leading-relaxed text-white/65"
                    >
                        Less stress, more systems, and real results. You focus on creating, we handle the rest.
                    </motion.p>
                </div>

                <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-12">
                    {PILLARS.map((pillar, i) => {
                        const Icon = ICONS[i];
                        return (
                            <motion.div
                                key={pillar.id}
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.55, delay: (i % 3) * 0.1 }}
                                data-testid={`pillar-card-${i}`}
                                className={`group relative overflow-hidden rounded-2xl border border-border bg-card/70 p-7 transition-[border-color,box-shadow] duration-300 ${SPANS[i]} ${ACCENTS[i % 3]}`}
                            >
                                <div className="flex items-start justify-between">
                                    <span
                                        className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${ICON_BG[i % 3]}`}
                                    >
                                        <Icon size={22} />
                                    </span>
                                    <span className="font-mono-tech text-xs text-white/25">{pillar.id}</span>
                                </div>
                                <h3 className="font-display mt-6 text-xl font-bold text-white">{pillar.name}</h3>
                                <p className="mt-1 font-mono-tech text-[11px] uppercase tracking-[0.15em] text-neon-blue/80">
                                    {pillar.tagline}
                                </p>
                                {pillar.description ? (
                                    <p className="mt-4 text-sm leading-relaxed text-white/70">{pillar.description}</p>
                                ) : (
                                    <ul className="mt-4 space-y-2.5">
                                        {pillar.bullets.map((b, bi) => (
                                            <li key={bi} className="flex items-start gap-2.5 text-sm leading-relaxed text-white/70">
                                                <Sparkle size={13} className={`mt-0.5 shrink-0 ${i % 3 === 0 ? "text-neon-purple" : i % 3 === 1 ? "text-neon-pink" : "text-neon-blue"}`} />
                                                {b}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                <div className="mt-6 flex items-center gap-4">
                                    <a
                                        {...typeformLinkProps}
                                        data-testid={`pillar-cta-${i}`}
                                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-neon-pink opacity-100 transition-opacity duration-300 md:opacity-0 md:group-hover:opacity-100"
                                    >
                                        {pillar.cta} <ArrowUpRight size={15} />
                                    </a>
                                    <button
                                        data-testid={`pillar-walk-${i}`}
                                        onClick={() => scrollToPillar(i)}
                                        className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] uppercase tracking-[0.15em] text-white/35 transition-colors duration-200 hover:text-neon-blue"
                                    >
                                        <Footprints size={13} /> Walk the robot here
                                    </button>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default PillarsSection;
